/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from './Icon';
import { Skeleton } from './States';

export interface KpiProps {
  icon?: LucideIcon;
  label: string;
  value: ReactNode;
  /** Quieter text after the value ("of 11"). */
  suffix?: ReactNode;
  /** One line under the value. */
  sub?: ReactNode;
  loading?: boolean;
}

/** A headline number: label, value in 26/32 tabular figures, one line of context. */
export function Kpi({ icon, label, value, suffix, sub, loading = false }: KpiProps) {
  return (
    <div className="ui-kpi" aria-busy={loading || undefined}>
      <div className="ui-kpi-label">{icon ? <Icon icon={icon} size="sm" /> : null}{label}</div>
      {loading ? (
        <>
          <div className="ui-kpi-value"><Skeleton size="xl" width="112px" /></div>
          <div className="ui-kpi-sub"><Skeleton size="xs" width="132px" /></div>
        </>
      ) : (
        <>
          <div className="ui-kpi-value">{value}{suffix ? <span className="ui-kpi-suffix"> {suffix}</span> : null}</div>
          {sub ? <div className="ui-kpi-sub">{sub}</div> : null}
        </>
      )}
    </div>
  );
}

export interface MeterProps {
  /** How full, from 0 to 1 (values outside are clamped). Not needed when `count` is given. */
  value?: number;
  /** A count against a total ("3 of 10 sent"): the bar fills by the share, and assistive tech hears the count itself. */
  count?: { done: number; total: number };
  /** What the meter measures, for assistive tech ("Today's spend, 31% of the cap"). */
  label: string;
  /** `data` (the default) for a share or a fill; `warn` and `bad` near and at a limit. */
  tone?: 'data' | 'accent' | 'ok' | 'warn' | 'bad';
  size?: 'sm' | 'md';
  className?: string;
}

/** A bar for a share or a fill level, or for progress through a count. */
export function Meter({ value, count, label, tone = 'data', size = 'md', className }: MeterProps) {
  const share = count ? (count.total > 0 ? count.done / count.total : 0) : (value ?? 0);
  const pct = Math.max(0, Math.min(100, Number.isFinite(share) ? share * 100 : 0));
  return (
    <span
      className={['ui-meter', `ui-meter--${tone}`, size === 'sm' ? 'is-sm' : '', className ?? ''].filter(Boolean).join(' ')}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={count ? count.total : 100}
      aria-valuenow={count ? count.done : Math.round(pct)}
    >
      {/* A computed width: the one inline style the kit allows. */}
      <span className="ui-meter-fill" style={{ width: `${pct}%` }} />
    </span>
  );
}
